import airport from '@rsos/assets/icons/src/mapSearchCategories/airport.svg';
import bridge from '@rsos/assets/icons/src/mapSearchCategories/bridge.svg';
import church from '@rsos/assets/icons/src/mapSearchCategories/church.svg';
import emsStation from '@rsos/assets/icons/src/mapSearchCategories/ems-station.svg';
import fireStation from '@rsos/assets/icons/src/mapSearchCategories/fire-station.svg';
import gasStation from '@rsos/assets/icons/src/mapSearchCategories/gas-station.svg';
import government from '@rsos/assets/icons/src/mapSearchCategories/government.svg';
import heliport from '@rsos/assets/icons/src/mapSearchCategories/heliport.svg';
import hospital from '@rsos/assets/icons/src/mapSearchCategories/hospital.svg';
import hydrant from '@rsos/assets/icons/src/mapSearchCategories/hydrant.svg';
import parcel from '@rsos/assets/icons/src/mapSearchCategories/parcel.svg';
import park from '@rsos/assets/icons/src/mapSearchCategories/park.svg';
import policeStation from '@rsos/assets/icons/src/mapSearchCategories/police-station.svg';
import powerPlant from '@rsos/assets/icons/src/mapSearchCategories/power-plant.svg';
import rail from '@rsos/assets/icons/src/mapSearchCategories/rail.svg';
import residence from '@rsos/assets/icons/src/mapSearchCategories/residence.svg';
import school from '@rsos/assets/icons/src/mapSearchCategories/school.svg';
import shelter from '@rsos/assets/icons/src/mapSearchCategories/shelter.svg';
import tower from '@rsos/assets/icons/src/mapSearchCategories/tower.svg';
import waterTreatment from '@rsos/assets/icons/src/mapSearchCategories/water-treatment.svg';

/**
 * One entry per silhouette icon in
 * `libs/assets/icons/src/mapSearchCategories/`. Consumers (the search
 * dropdown, primarily) substring-match each `keyword` against a GIS
 * layer title — case-insensitive — to pick the icon for a result row.
 *
 * Keep `keywords` lowercase. They're matched as plain substrings, so
 * ordering inside this array determines tie-breaks: the first entry
 * whose keyword list contains a hit wins. Put more specific
 * categories before more generic ones (e.g. `fire-station` before a
 * hypothetical `fire` entry) so the wrong icon doesn't preempt the
 * right one. Categories that need to win against each other for the
 * same word (`heliport` vs. `airport`) live in the order a designer
 * would expect to see picked.
 */
export interface MapSearchCategoryIcon {
  /** Stable category key — matches the SVG basename (kebab-case). */
  key: string;
  /** Bundled URL to the SVG asset. */
  icon: string;
  /**
   * Substrings checked against a layer title (lowercased). A match
   * means this icon represents that layer.
   */
  keywords: readonly string[];
}

/**
 * Source of truth for category → icon + keyword vocab. Mirrors the
 * design-review preview at
 * `libs/assets/icons/src/mapSearchCategories/preview.html`; whenever a
 * keyword is added here it should be added there too (or vice-versa)
 * so the preview keeps showing what's actually wired up.
 */
export const MAP_SEARCH_CATEGORY_ICONS = [
  {
    key: 'hydrant',
    icon: hydrant,
    keywords: ['hydrant'],
  },
  {
    key: 'parcel',
    icon: parcel,
    keywords: ['parcel', 'apn', 'lot', 'plat', 'property'],
  },
  {
    key: 'residence',
    icon: residence,
    keywords: ['residence', 'address', 'home', 'dwelling', 'house'],
  },
  {
    key: 'school',
    icon: school,
    keywords: [
      'school',
      'elementary',
      'middle',
      'high',
      'university',
      'college',
      'preschool',
      'daycare',
      'academy',
    ],
  },
  {
    key: 'hospital',
    icon: hospital,
    keywords: [
      'hospital',
      'clinic',
      'urgent care',
      'er',
      'icu',
      'medical center',
    ],
  },
  {
    key: 'ems-station',
    icon: emsStation,
    keywords: ['ems', 'ambulance', 'paramedic', 'medic', 'first aid'],
  },
  {
    key: 'shelter',
    icon: shelter,
    keywords: ['shelter', 'refuge', 'evacuation', 'safe haven'],
  },
  // `fire-station` is intentionally listed before any future `fire`
  // entry: a layer titled "Fire Station 12" must resolve to the
  // station icon, not a generic flame.
  {
    key: 'fire-station',
    icon: fireStation,
    keywords: ['fire station', 'fire dept', 'firehouse', 'fire'],
  },
  {
    key: 'police-station',
    icon: policeStation,
    keywords: ['police station', 'police dept', 'sheriff', 'precinct'],
  },
  {
    key: 'church',
    icon: church,
    keywords: [
      'church',
      'temple',
      'mosque',
      'synagogue',
      'chapel',
      'cathedral',
    ],
  },
  {
    key: 'government',
    icon: government,
    keywords: [
      'government',
      'city hall',
      'courthouse',
      'municipal',
      'civic',
      'federal',
    ],
  },
  {
    key: 'park',
    icon: park,
    keywords: ['park', 'greenspace', 'recreation', 'trail', 'forest'],
  },
  {
    key: 'gas-station',
    icon: gasStation,
    keywords: ['gas station', 'fuel', 'petrol', 'filling station'],
  },
  // `airport` lists `heliport` in its synonym set per design, but we
  // keep `heliport` ordered after `airport` here so that a layer
  // titled simply "Heliports" still wins via its own dedicated icon.
  // (Substring matcher walks this array head-to-tail.)
  {
    key: 'airport',
    icon: airport,
    keywords: ['airport', 'airfield', 'runway'],
  },
  {
    key: 'heliport',
    icon: heliport,
    keywords: ['heliport', 'helipad', 'helicopter', 'landing zone', 'lz'],
  },
  {
    key: 'rail',
    icon: rail,
    keywords: ['rail', 'railroad', 'train', 'station', 'track', 'transit'],
  },
  {
    key: 'bridge',
    icon: bridge,
    keywords: ['bridge', 'overpass', 'viaduct'],
  },
  {
    key: 'tower',
    icon: tower,
    keywords: ['tower', 'cell tower', 'radio tower', 'antenna'],
  },
  {
    key: 'power-plant',
    icon: powerPlant,
    keywords: ['power plant', 'substation', 'electric', 'utility', 'factory'],
  },
  {
    key: 'water-treatment',
    icon: waterTreatment,
    keywords: ['water treatment', 'wastewater', 'sewage', 'reservoir'],
  },
] as const satisfies readonly MapSearchCategoryIcon[];

/** Type-level union of every category key declared above. */
export type MapSearchCategoryKey =
  (typeof MAP_SEARCH_CATEGORY_ICONS)[number]['key'];

/**
 * Resolve a single category-icon URL for an arbitrary text snippet
 * (typically a GIS layer / sublayer title, but any string works).
 *
 * The match is plain case-insensitive substring containment, walking
 * `MAP_SEARCH_CATEGORY_ICONS` head-to-tail and returning the first
 * entry whose keyword list lands a hit. Returns `undefined` when no
 * keyword matches so callers can render a generic fallback icon.
 *
 * Multiple text fragments can be passed (e.g. layer title + spatial
 * object name + kind) and the first non-empty fragment that yields a
 * match wins. This lets call sites supply a prioritized list of
 * sources without each having to short-circuit themselves.
 */
export function resolveCategoryIcon(
  ...candidates: readonly (string | null | undefined)[]
): string | undefined {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const haystack = candidate.toLowerCase();
    for (const cat of MAP_SEARCH_CATEGORY_ICONS) {
      if (cat.keywords.some(k => haystack.includes(k))) return cat.icon;
    }
  }
  return undefined;
}
